// =============================================================================
// src/data/atalaia-mapping.ts — Tradutor PURO da camada `gold` do AtalaIA para o
//   formato do dataset da tela "Explorar". FONTE UNICA da logica de mapeamento:
//   o cliente runtime (atalaia-client.ts) e o gerador build-time
//   (scripts/atalaia-source.mjs) consomem este modulo — zero duplicacao (DRY).
//
// Proveniencia: portado de scripts/atalaia-source.mjs (que virou shim re-exportando
//   este arquivo, executado por tsx no build). SRP: so converte; NAO fala com a rede
//   (o fetch fica no client/shim). Sem DOM, sem Leaflet.
//
// Cobertura real hoje (confirmada no banco 2026-06-17):
//   - el: 2016/2020/2024 = Prefeito, 2022 = Governador. 2014/2018 NAO carregados.
//   - idh: PNUD/Atlas Brasil, mais recente = 2010.
// =============================================================================

import type {
  AmazonasRegion,
  AtalaiaEducationRow,
  AtalaiaElectionRow,
  AtalaiaTerritoryRow,
  ExploreElectionResult,
  MunicipalityId,
  PartySigla,
} from '../app/types';

/** Os 4 anos com resultado `el` oficial no AtalaIA. */
export type AtalaiaYear = '2016' | '2020' | '2022' | '2024';

/** Anos com resultado `el` oficial no AtalaIA + o cargo agregado em cada um. */
export const OFFICE_BY_YEAR_ATALAIA: Record<AtalaiaYear, 'prefeito' | 'governador'> = {
  '2016': 'prefeito',
  '2020': 'prefeito',
  '2022': 'governador',
  '2024': 'prefeito',
};
export const ATALAIA_YEARS = Object.keys(OFFICE_BY_YEAR_ATALAIA) as readonly AtalaiaYear[];

/**
 * party_code do TSE (maiusculo, com nomes historicos) -> sigla do union
 * PartySigla do dataset. Predecessores colapsam no sucessor (PMDB->MDB, PR->PL,
 * PRB->Republicanos, PSL/DEM->UB). Fora do mapa vira 'outros' (sigla valida).
 */
const ATALAIA_PARTY: Record<string, PartySigla> = {
  AVANTE: 'Avante',
  MDB: 'MDB', PMDB: 'MDB',
  PT: 'PT',
  PSDB: 'PSDB',
  PSD: 'PSD',
  PL: 'PL', PR: 'PL',
  PP: 'PP', PPB: 'PP',
  PSB: 'PSB',
  REPUBLICANOS: 'Republicanos', PRB: 'Republicanos',
  'UNIÃO': 'UB', DEM: 'UB', PSL: 'UB',
};

/** Normaliza a sigla do AtalaIA p/ a forma do dataset; desconhecida -> 'outros'. */
export function normalizeAtalaiaParty(code: string): PartySigla {
  return ATALAIA_PARTY[String(code).trim().toUpperCase()] ?? 'outros';
}

/** Resultado `el` de um municipio num ano: turno decisivo (round max), top-2 votos. */
function elResultForYear(
  elections: readonly AtalaiaElectionRow[],
  year: AtalaiaYear,
): ExploreElectionResult | null {
  const office = OFFICE_BY_YEAR_ATALAIA[year];
  const rows = elections.filter((r) => String(r.year) === year && r.office === office);
  if (rows.length === 0) return null;
  const maxRound = Math.max(...rows.map((r) => Number(r.round)));
  const finalRound = rows.filter((r) => Number(r.round) === maxRound)
    .sort((a, b) => Number(b.votes) - Number(a.votes));
  // finalRound nao-vazio: maxRound veio de `rows` (≥1 linha casa o turno).
  const first = finalRound[0] as AtalaiaElectionRow;
  const second = finalRound[1];
  return {
    p: normalizeAtalaiaParty(first.party_code),
    pct: Math.round(Number(first.vote_pct)),
    s: normalizeAtalaiaParty((second ?? first).party_code),
  };
}

/** { ano -> { id IBGE -> {p,pct,s} } } a partir do array gold_territory. */
export function buildElResultsByYear(
  territories: readonly AtalaiaTerritoryRow[],
): Record<AtalaiaYear, Record<MunicipalityId, ExploreElectionResult>> {
  const byYear = {} as Record<AtalaiaYear, Record<MunicipalityId, ExploreElectionResult>>;
  for (const year of ATALAIA_YEARS) byYear[year] = {};
  for (const t of territories) {
    for (const year of ATALAIA_YEARS) {
      const r = elResultForYear(t.election_results, year);
      if (r) byYear[year][t.official_code] = r;
    }
  }
  return byYear;
}

/** { id IBGE -> eleitorado em milhares } do TSE perfil_eleitorado mais recente. */
export function buildEleitoresKById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, number> {
  const out: Record<MunicipalityId, number> = {};
  for (const t of territories) {
    const electors = t.electorates[0]?.electors;
    if (electors != null) out[t.official_code] = Math.round(Number(electors) / 1000);
  }
  return out;
}

/** { id IBGE -> idh } (2 casas) do human_developments mais recente de cada muni. */
export function buildIdhById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, number> {
  const out: Record<MunicipalityId, number> = {};
  for (const t of territories) {
    const idh = t.human_developments[0]?.idh_m;
    if (idh == null) continue;
    out[t.official_code] = Math.round(Number(idh) * 100) / 100;
  }
  return out;
}

// ---------- Eixos tematicos `th` derivados de indicadores reais (0..3) ---------

/** Quantil q (0..1) de uma lista JA ordenada asc (interpolacao linear). */
function quantile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  // base ∈ [0, len-1] -> sempre definido (precedente: quartilesOf em explore-map).
  const lo = sorted[base] as number;
  const next = sorted[base + 1];
  return next !== undefined ? lo + (pos - base) * (next - lo) : lo;
}

/**
 * Bucketiza { id -> valor } em 0..3 pelos quartis (q25/q50/q75) da distribuicao —
 * mesma logica relativa dos coropletos de densidade/populacao da tela.
 */
export function bucketizeByQuartile(
  valueById: Record<MunicipalityId, number>,
): Record<MunicipalityId, number> {
  const vals = Object.values(valueById).sort((a, b) => a - b);
  const q: readonly [number, number, number] = [
    quantile(vals, 0.25), quantile(vals, 0.5), quantile(vals, 0.75),
  ];
  const out: Record<MunicipalityId, number> = {};
  for (const [id, v] of Object.entries(valueById)) out[id] = v < q[0] ? 0 : v < q[1] ? 1 : v < q[2] ? 2 : 3;
  return out;
}

/** IDEB representativo (rede publica > municipal, ano mais recente) por municipio. */
export function buildIdebById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, number> {
  const out: Record<MunicipalityId, number> = {};
  for (const t of territories) {
    const rows = t.education_indicators;
    if (rows.length === 0) continue;
    const maxYear = Math.max(...rows.map((r) => Number(r.year)));
    const latest = rows.filter((r) => Number(r.year) === maxYear);
    // latest nao-vazio: maxYear veio de `rows`.
    const first = latest[0] as AtalaiaEducationRow;
    const pick = latest.find((r) => r.school_type === 'pública') ?? first;
    out[t.official_code] = Number(pick.ideb_score);
  }
  return out;
}

/** Renda per capita (RDPC, PNUD) mais recente por municipio. */
export function buildRdpcById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, number> {
  const out: Record<MunicipalityId, number> = {};
  for (const t of territories) {
    const r = t.human_developments[0]?.per_capita_income;
    if (r != null) out[t.official_code] = Number(r);
  }
  return out;
}

/** Cobertura do Bolsa Familia (beneficiarios / populacao) — proxy de vulnerabilidade. */
export function buildBfCoverageById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, number> {
  const out: Record<MunicipalityId, number> = {};
  for (const t of territories) {
    const benef = t.social_programs[0]?.beneficiaries;
    const pop = t.territory_snapshots[0]?.population;
    if (benef == null) continue;
    if (!pop) continue;
    out[t.official_code] = Number(benef) / Number(pop);
  }
  return out;
}

// ---------- Subdivisoes territoriais (IBGE meso/microrregiao) ------------------

/** As 4 mesorregioes do AM = valores validos do union AmazonasRegion no frontend. */
const AM_REGIONS = new Set<string>([
  'Centro Amazonense', 'Norte Amazonense', 'Sul Amazonense', 'Sudoeste Amazonense',
]);

/** Type guard: o texto e uma das 4 mesorregioes validas do AM. */
function isAmazonasRegion(meso: string | null): meso is AmazonasRegion {
  return meso != null && AM_REGIONS.has(meso);
}

/** { id -> mesorregiao } so com valores validos de AmazonasRegion (defensivo). */
export function buildRegionById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, AmazonasRegion> {
  const out: Record<MunicipalityId, AmazonasRegion> = {};
  for (const t of territories) if (isAmazonasRegion(t.mesoregion)) out[t.official_code] = t.mesoregion;
  return out;
}

/** { id -> microrregiao } do IBGE. */
export function buildMicroById(
  territories: readonly AtalaiaTerritoryRow[],
): Record<MunicipalityId, string> {
  const out: Record<MunicipalityId, string> = {};
  for (const t of territories) if (t.microregion) out[t.official_code] = t.microregion;
  return out;
}
